<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { useForm, useFieldValue } from 'vee-validate';
import {
  commercialTypeOptions,
  customerErhlPropertyUsedOptions,
  ErhlPropertyType,
  erhlPropertyTypeOptions,
  erhlSupportingPropertyTypeOptions,
  PropertyPurpose,
  residentialCommercialTypes,
} from '@/helpers/const';
import { erhlSupportingPropertySchema, erhlPurchasePropertySchema } from '@/validation';

import PropertyToPurchaseFields from './PropertyToPurchaseFields.vue';
import SupportingPropertyFields from './SupportingPropertyFields.vue';

import type { PropertyDetailEntry } from '@/types';

export type ErhlPropertyMode = 'supporting' | 'purchase';

const props = defineProps<{
  modelValue: PropertyDetailEntry;
  propertyMode: ErhlPropertyMode;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PropertyDetailEntry): void;
}>();

const validationSchema = computed(() => {
  switch (props.propertyMode) {
    case 'purchase':
      return erhlPurchasePropertySchema;
    case 'supporting':
    default:
      return erhlSupportingPropertySchema;
  }
});

const { meta, values, handleSubmit, resetForm, setFieldValue } = useForm<PropertyDetailEntry>({
  validateOnMount: false,
  validationSchema,
});

const idField = useFieldValue<string>('id');

const baseId = computed(() => {
  const index = idField.value?.split('-').pop() ?? '0';
  const prefix = props.propertyMode === 'purchase' ? 'purchase-property' : 'supporting-property';
  return `${prefix}-${index}`;
});

const hasSubmitError = ref(false);

/**
 * Returns the property type dropdown options based on the property mode.
 * Supporting properties exclude retirement/land-lease (purchase-only option).
 */
const propertyTypeOptions = computed(() => {
  switch (props.propertyMode) {
    case 'purchase':
      return erhlPropertyTypeOptions;
    case 'supporting':
    default:
      return erhlSupportingPropertyTypeOptions;
  }
});

/** True when the selected property type is retirement, land-lease or community home */
const isRetirementPropertyType = computed(
  () => values.propertyType === ErhlPropertyType.RETIREMENT_LAND_LEASE_COMMUNITY_HOME,
);

const commercialTypeTooltips: Record<string, string> = {
  SPECIALISED_USE: 'Specialised Use includes childcare, gaming zone, medical clinic, and service stations.',
};

const update = handleSubmit(
  () => {
    hasSubmitError.value = false;
    emit('update:modelValue', { ...values });
    return true;
  },
  () => {
    hasSubmitError.value = true;
    return false;
  },
);

watch(
  () => values.propertyType,
  () => {
    hasSubmitError.value = false;
  },
);

const cancel = () => {
  resetForm();
};

watch(
  () => props.modelValue,
  (val) => {
    const initialValues = { ...val };

    if (
      initialValues.propertyType === ErhlPropertyType.COMMERCIAL &&
      residentialCommercialTypes.includes(initialValues.commercialType as (typeof residentialCommercialTypes)[number])
    ) {
      initialValues.propertyPurpose = PropertyPurpose.INVESTMENT_PROPERTY;
    }

    resetForm({ values: initialValues });
  },
  { immediate: true },
);

watch(
  () => [values.propertyType, values.commercialType] as const,
  async ([propertyType, commercialType]) => {
    if (
      propertyType === ErhlPropertyType.COMMERCIAL &&
      residentialCommercialTypes.includes(commercialType as (typeof residentialCommercialTypes)[number])
    ) {
      await nextTick();
      setFieldValue('propertyPurpose', PropertyPurpose.INVESTMENT_PROPERTY);
    }
  },
  { flush: 'post' },
);

defineExpose({ update, cancel });
</script>

<template>
  <form>
    <div class="property-details-form">
      <BxField v-slot="{ field }" name="propertyType">
        <BxSelect
          :id="baseId + '-property-type'"
          :list-items="propertyTypeOptions"
          v-bind="field"
          class="form-control"
          label="Property type"
          label-type="floating"
          placeholder="Select property type..."
        />
      </BxField>

      <div v-if="values.propertyType === ErhlPropertyType.COMMERCIAL" class="form-control">
        <BxField v-slot="{ field }" name="commercialType">
          <BxSelect
            :id="baseId + '-commercial-type'"
            :list-items="commercialTypeOptions"
            :option-tooltips="commercialTypeTooltips"
            v-bind="field"
            label="Commercial Type"
            label-type="floating"
            placeholder="Select commercial type..."
          />
        </BxField>
      </div>
      <BxField v-slot="{ field }" name="address">
        <BxAddressInput
          :id="baseId + '-property-address'"
          v-bind="field"
          class="form-control"
          label="Property address"
          label-type="floating"
        />
      </BxField>
      <BxField v-if="!isRetirementPropertyType" v-slot="{ field }" name="propertyPurpose">
        <BxRadio
          :id="baseId + '-property-used'"
          :options="customerErhlPropertyUsedOptions"
          v-bind="field"
          class="form-control"
          label="Property purpose"
          label-type="default"
          block
        />
      </BxField>
      <PropertyToPurchaseFields
        v-if="propertyMode === 'purchase'"
        :id="baseId"
        :contract-for-sale="values.contractForSale"
        :receive-rental-income="values.receiveRentalIncome"
        :property-purpose="values.propertyPurpose"
        :is-retirement-property-type="isRetirementPropertyType"
        :is-new-build="values.isNewBuild"
        :erhl-savings-amount="values.erhlSavingsAmount"
      />
      <SupportingPropertyFields
        v-if="propertyMode === 'supporting'"
        :id="baseId"
        :property-purpose="values.propertyPurpose"
        :receive-rental-income="values.receiveRentalIncome"
        :is-new-build="values.isNewBuild"
      />
    </div>
    <BxErrorPanel v-show="!meta.valid && hasSubmitError" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>
  </form>
</template>

<style scoped>
.property-details-form :deep(.form-control) {
  padding-right: 2.5rem;
}
</style>
